import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosResponse } from 'axios';
import { MicroInteractionButton } from '../../molecules/MicroInteractionButton/MicroInteractionButton';
import Form, { FormTemplate, FormContainerModifier } from '../../molecules/Form';
import { Icons, SVGIcon } from '../../atoms/SVG';
import { BtnModifier } from '../../molecules/Btn';
import { InteractionStatus } from '../../../types/InteractionStatus';
import { sendLiveRegionMessage } from '../../atoms/LiveRegionGlobal/LiveRegionGlobal';
import { useInvalidateRefreshableBlock } from '../../../_lib/BlockFetcher';
import IForm from '../../../types/IForm';
import { TooltipPlacement } from '../../atoms/Tooltip';

const additionalLocalStatus = {
  FAVORITE: 'FAVORITE',
  NO_FAVORITE: 'NO_FAVORITE',
};

export type FavoriteButtonProps = {
  id: string;
  favorite: { form: IForm; active: boolean };
  translationNamespace: 'PageInteractions' | 'PersonInteraction';
  favoriteText: string;
  noFavoriteText: string;
  svgspriteId: SVGIcon;
  color: BtnModifier.MINOR_ACTION_ACTIVATED_PETROL | BtnModifier.MINOR_ACTION_ACTIVATED_RED;
  resourceToInvalidate: 'favoritedpages' | 'favoritedusers';
};

export function FavoriteButton(props: FavoriteButtonProps) {
  const { t } = useTranslation();
  const [favorite, setFavorite] = useState(props.favorite);
  const [currentStatus, setCurrentStatus] = useState(
    props.favorite.active ? additionalLocalStatus.FAVORITE : additionalLocalStatus.NO_FAVORITE,
  );
  const invalidateRefreshableBlock = useInvalidateRefreshableBlock();

  const statusMapping = {
    [InteractionStatus.SENDING]: t(`${props.translationNamespace}:status.sending`),
    [InteractionStatus.FAIL]: t(`${props.translationNamespace}:failure.statusMessage`),
    [additionalLocalStatus.NO_FAVORITE]: t(`${props.translationNamespace}:success.deleted`),
    [additionalLocalStatus.FAVORITE]: t(`${props.translationNamespace}:success.added`),
  };

  /**
   * Special update as we need to watch for props changes outside from events.
   * This is for displaying the loading spinner on state change for microtransaction.
   *
   * Needs refactoring, as the pageinteractionsWatchButton is the same
   */
  useEffect(() => {
    // check is valid as it may come from Event
    setFavorite(props.favorite);
    setCurrentStatus(props.favorite.active ? additionalLocalStatus.FAVORITE : additionalLocalStatus.NO_FAVORITE);
  }, [props.favorite]);

  const onSubmit = (event: any, model: any) => {
    if (event) {
      event.preventDefault();
    }
    sendLiveRegionMessage(statusMapping[InteractionStatus.SENDING]);

    model
      .submit()
      .then((response: AxiosResponse) => {
        invalidateRefreshableBlock(props.resourceToInvalidate);
        const statusOfResponse = response.data.active
          ? additionalLocalStatus.FAVORITE
          : additionalLocalStatus.NO_FAVORITE;
        sendLiveRegionMessage(statusMapping[statusOfResponse]);
        setFavorite(response.data);
        setCurrentStatus(statusOfResponse);
      })
      .catch(() => {
        sendLiveRegionMessage(statusMapping[InteractionStatus.FAIL]);
      });
  };

  const getCurrentInteraction = (modelStatus: any) => {
    if (modelStatus === InteractionStatus.SUCCESS || modelStatus === InteractionStatus.NONE) {
      return currentStatus;
    }
    return modelStatus;
  };

  const renderFormTemplate = (viewModel: any) => {
    return (
      <MicroInteractionButton
        type="submit"
        modifier={[BtnModifier.MICRO_INTERACTION, BtnModifier.TRANSPARENT, BtnModifier.MINOR_ACTION]}
        svgSpriteId={props.svgspriteId}
        hiddenText
        // this will reset the liveregion, fixing SON-7807. But this whole construct needs to be refactored...
        text={props.noFavoriteText}
        titleText={props.noFavoriteText}
        tooltipPlacement={TooltipPlacement.TOP}
        currentInteraction={getCurrentInteraction(viewModel.status)}
        interactions={{
          NO_FAVORITE: {
            svgSpriteId: props.svgspriteId,
            modifier: [BtnModifier.MICRO_INTERACTION, BtnModifier.TRANSPARENT, BtnModifier.MINOR_ACTION],
            text: props.noFavoriteText,
            titleText: props.noFavoriteText,
          },
          FAVORITE: {
            svgSpriteId: props.svgspriteId,
            modifier: [
              BtnModifier.MICRO_INTERACTION,
              BtnModifier.TRANSPARENT,
              BtnModifier.MINOR_ACTION,
              BtnModifier.MINOR_ACTION_ACTIVATED,
              props.color,
            ],
            text: props.favoriteText,
            titleText: props.favoriteText,
          },
          [InteractionStatus.FAIL]: {
            svgSpriteId: Icons.FEEDBACK_ERROR,
            modifier: [
              BtnModifier.MICRO_INTERACTION,
              BtnModifier.TRANSPARENT,
              BtnModifier.MINOR_ACTION,
              BtnModifier.MINOR_ACTION_FAILED,
            ],
            text: t(`${props.translationNamespace}:failure.buttonText`),
            titleText: t(`${props.translationNamespace}:failure.buttonTitleText`),
          },
          [InteractionStatus.SENDING]: {
            modifier: [
              BtnModifier.MICRO_INTERACTION,
              BtnModifier.TRANSPARENT,
              BtnModifier.MINOR_ACTION,
              BtnModifier.MINOR_ACTION_BUSY,
            ],
            text: t(`${props.translationNamespace}:status.sending`),
            titleText: null,
          },
        }}
      />
    );
  };

  return (
    <div className="page-interactions__button-container">
      <Form
        key={currentStatus}
        id={props.id}
        model={favorite.form}
        onSubmit={onSubmit}
        containerModifier={FormContainerModifier.NO_PADDING}
        allowNavigationOnUnsavedChanges
      >
        <FormTemplate>{renderFormTemplate}</FormTemplate>
      </Form>
    </div>
  );
}
